import type { CSSProperties } from 'react';
import { Icon } from './ui/Icon.js';
import { AgeSlider } from './AgeSlider.js';

export interface FamilyMember {
  label: string | null;
  age: number;
}

interface FamilyRosterFieldProps {
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  members: FamilyMember[];
  onMembersChange: (next: FamilyMember[]) => void;
  maxMembers?: number;
}

const labelInputStyle: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
  flex: 1,
  minWidth: 0,
};

export function FamilyRosterField({
  enabled,
  onEnabledChange,
  members,
  onMembersChange,
  maxMembers = 20,
}: FamilyRosterFieldProps) {
  const hasInfant = members.some((m) => m.age === 0);
  const hasYoungChild = members.some((m) => m.age >= 1 && m.age <= 8);

  function updateMember(idx: number, patch: Partial<FamilyMember>) {
    const next = [...members];
    next[idx] = { ...next[idx], ...patch };
    onMembersChange(next);
  }

  function removeMember(idx: number) {
    onMembersChange(members.filter((_, i) => i !== idx));
  }

  function addMember() {
    if (members.length >= maxMembers) return;
    onMembersChange([...members, { label: null, age: 5 }]);
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p style={{ color: 'var(--ink)', fontSize: 14, fontWeight: 500 }}>Family roster</p>
          <p className="mt-0.5" style={{ color: 'var(--ink-4)', fontSize: 12 }}>
            Individual ages for smarter suggestions
          </p>
        </div>
        <button
          type="button"
          onClick={() => onEnabledChange(!enabled)}
          role="switch"
          aria-checked={enabled}
          className="relative rounded-full transition-colors"
          style={{
            width: 44, height: 24,
            background: enabled ? 'var(--accent)' : 'var(--surface-3)',
            border: '1px solid var(--line-soft)',
          }}
        >
          <span
            className="absolute top-0.5 rounded-full transition-all"
            style={{
              left: enabled ? 22 : 2,
              width: 18, height: 18,
              background: 'var(--surface)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            }}
          />
        </button>
      </div>

      {enabled && (
        <div className="mt-5 space-y-2">
          {members.map((member, idx) => (
            <div
              key={idx}
              className="space-y-2 p-3"
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--line-soft)',
                borderRadius: 12,
              }}
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder={`Member ${idx + 1} name (optional)`}
                  maxLength={30}
                  value={member.label ?? ''}
                  onChange={(e) => updateMember(idx, { label: e.target.value || null })}
                  style={labelInputStyle}
                />
                <button
                  type="button"
                  onClick={() => removeMember(idx)}
                  aria-label="Remove member"
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: 'transparent', color: 'var(--ink-4)', cursor: 'pointer',
                  }}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
              <AgeSlider
                value={member.age}
                onChange={(age) => updateMember(idx, { age })}
                ariaLabel={member.label ? `${member.label} age` : `Member ${idx + 1} age`}
              />
            </div>
          ))}

          {hasInfant && (
            <p className="text-xs" style={{ color: 'var(--warning)' }}>
              Infant detected — enable Baby Led Weaning mode?
            </p>
          )}
          {hasYoungChild && (
            <p className="text-xs" style={{ color: 'var(--accent)' }}>
              Young children detected — enable picky eater suggestions?
            </p>
          )}

          {members.length < maxMembers && (
            <button
              type="button"
              onClick={addMember}
              className="text-xs font-medium hover:underline inline-flex items-center gap-1 mt-1"
              style={{ color: 'var(--accent)' }}
            >
              <Icon name="plus" size={12} stroke={2.4} />
              Add member
            </button>
          )}
        </div>
      )}
    </div>
  );
}
